import type { FragmentType } from '~/graphql/generated';
import type { Badge, BadgeDetail, BadgeListItem } from '~/types/Award/badge';

import { adminBadgeFragment, adminBadgeListFragment, badgeMetaFragment } from '~/graphql/documents/award';
import { awardWalletBadgeDetailFragment, awardWalletBadgeListFragment } from '~/graphql/documents/awardWallet';
import { adminContentFileFragment } from '~/graphql/documents/content';
import { publicFileFragment } from '~/graphql/documents/core';
import { useFragment } from '~/graphql/generated';
import { mapSpotlight } from '~/utils/unified/mapSpotlight';

import { transformCoinValue } from './learningContentTransformers';
import { transformAdminPartnerReference, transformPartnerReference } from './partnerTransformers';

export const transformAdminBadge = (fragment: FragmentType<typeof adminBadgeFragment> | null | undefined): Badge | null => {
  const { mapPublicFragmentToUpload } = useV2CoreFileUpload();
  if (!fragment) {
    return null;
  }

  const badge = useFragment(adminBadgeFragment, fragment);
  const badgeMeta = badge.meta && useFragment(badgeMetaFragment, badge.meta);
  const partner = badge.partner && transformAdminPartnerReference(badge.partner);

  return {
    ...badge,
    partner: partner || null,
    iconFile: badge.iconFile ? mapPublicFragmentToUpload(useFragment(adminContentFileFragment, badge.iconFile)) : null,
    coverImage: badge.coverImage ? mapPublicFragmentToUpload(useFragment(adminContentFileFragment, badge.coverImage)) : null,
    premiumBadgeFile: badge.premiumBadgeFile ? mapPublicFragmentToUpload(useFragment(adminContentFileFragment, badge.premiumBadgeFile)) : null,
    meta: badgeMeta
      ? { description: badgeMeta.description || null, disclaimer: badgeMeta.disclaimer || null }
      : null,
  };
};

export const transformAdminBadgeListItem = (fragment: FragmentType<typeof adminBadgeListFragment>): BadgeListItem => {
  const badge = useFragment(adminBadgeListFragment, fragment);
  const partner = badge.partner && transformAdminPartnerReference(badge.partner);

  return {
    ...badge,
    partner: partner || null,
  };
};

export const transformAwardWalletBadgeListItem = (fragment: FragmentType<typeof awardWalletBadgeListFragment>): Badge => {
  const { mapPublicFragmentToUpload } = useV2CoreFileUpload();
  const badge = useFragment(awardWalletBadgeListFragment, fragment);
  const partner = badge.partner && transformPartnerReference(badge.partner);
  return {
    ...badge,
    partner: partner || null,
    iconFile: badge.iconFile ? mapPublicFragmentToUpload(useFragment(publicFileFragment, badge.iconFile)) : null,
    coverImage: badge.coverImage ? mapPublicFragmentToUpload(useFragment(publicFileFragment, badge.coverImage)) : null,
  };
};

export const transformAwardWalletBadge = (fragment: FragmentType<typeof awardWalletBadgeDetailFragment>): BadgeDetail => {
  const { mapPublicFragmentToUpload } = useV2CoreFileUpload();
  const badge = useFragment(awardWalletBadgeDetailFragment, fragment);
  const badgeMeta = badge.meta && useFragment(badgeMetaFragment, badge.meta);
  const partner = badge.partner && transformPartnerReference(badge.partner);

  return {
    ...badge,
    learningAssetCollections: badge.learningAssetCollections?.map(collection => ({
      id: collection.id,
      name: collection.name,
      slug: collection.slug,
      logoImage: collection.logoImage ? mapPublicFragmentToUpload(useFragment(publicFileFragment, collection.logoImage)) : null,
      learningAssets: collection.learningAssets?.map(learningAsset => ({
        id: learningAsset.id,
        name: learningAsset.name,
        coinValue: (learningAsset.coinValue && transformCoinValue(learningAsset.coinValue)) ?? null,
        publishedSkills: learningAsset.publishedSkills || [],
        currentUserProgress: learningAsset.currentUserProgress
          ? {
              id: learningAsset.currentUserProgress.id,
              completedAt: learningAsset.currentUserProgress.completedAt || null,
            }
          : null,
      })) ?? [],
      activeCampaign: collection.activeCampaign
        ? { id: collection.activeCampaign.id, name: collection.activeCampaign.name, hasPrize: collection.activeCampaign.hasPrize }
        : null,
      spotlight: mapSpotlight(collection.spotlight),
    })) ?? [],
    iconFile: badge.iconFile ? mapPublicFragmentToUpload(useFragment(publicFileFragment, badge.iconFile)) : null,
    coverImage: badge.coverImage ? mapPublicFragmentToUpload(useFragment(publicFileFragment, badge.coverImage)) : null,
    premiumBadgeFile: badge.premiumBadgeFile ? mapPublicFragmentToUpload(useFragment(publicFileFragment, badge.premiumBadgeFile)) : null,
    meta: badgeMeta
      ? { description: badgeMeta.description || null, disclaimer: badgeMeta.disclaimer || null }
      : null,
    partner: partner || null,
  };
};
